<script setup>
import "vue-tel-input/vue-tel-input.css";
import { LoadingOutlined } from "@ant-design/icons-vue";
import { h } from "vue";

const seoTitle = "MIRASOFT - Contact";
const seoDescription = "";

useHead({
  title: seoTitle,
  meta: [
    { name: "description", content: seoDescription },
    { name: "og:title", content: seoTitle },
    { name: "og:description", content: seoDescription },
    { name: "keywords", content: "Contact" },
  ],
});

const config = useRuntimeConfig();

const formState = reactive({
  fullname: "",
  email: "",
  phoneNumber: "",
  message: "",
  howDidYouFindUs: null,
});

const contacts = [
  {
    title: "PHONE",
    content: "+84 935 10 3636",
    icon: "/images/contact/phone.svg",
  },
  {
    title: "TELEPHONE",
    content: "+84 24 36 436 999",
    icon: "/images/contact/telephone.svg",
  },
  {
    title: "Email",
    content: "contact@mirasoft.tech",
    icon: "/images/contact/email.svg",
  },
];

const validatorPhonenumber = async (_rule, value) => {
  const phoneRegex = /^\+\d+([ -]\d+)*$/;
  if (value.length === 0) {
    return Promise.reject("Please fill the required field.");
  }
  if (errors.value.phoneNumber) {
    return Promise.reject(errors.value.phoneNumber);
  }
  if (!phoneRegex.test(value)) {
    return Promise.reject("Invalid phone number");
  }
  return Promise.resolve();
};

const validatorEmail = async (_rule, value) => {
  if (errors.value.email) {
    return Promise.reject(errors.value.email);
  }
  return Promise.resolve();
};

const rules = {
  fullname: { required: true, message: "Please fill the required field." },
  phoneNumber: [{ required: true }, { validator: validatorPhonenumber }],
  email: [
    { required: true, message: "Please fill the required field." },
    { type: "email", message: "Please fill the required field." },
    { validator: validatorEmail },
  ],
};

const loading = ref(false);
const errors = ref({});
const formRef = ref();
const visibleResultModal = ref(false);

const onSubmit = async () => {
  loading.value = true;
  try {
    const response = await $fetch("/mirasoft/contact", {
      baseURL: config.public.baseUrl,
      method: "POST",
      body: toRaw(formState),
      headers: {
        Accept: "application/json",
      },
    });
    loading.value = false;
    formRef.value.resetFields();
    visibleResultModal.value = true;
  } catch (error) {
    errors.value = error.data.error;
    formRef.value.validateFields();
    loading.value = false;
    console.log(error);
  }
};

// custom antd loading
const indicator = h(LoadingOutlined, {
  style: {
    fontSize: "24px",
  },
  spin: true,
});
</script>

<template id="contact-us">
  <div class="px-10 max-md:px-6 max-md:py-0 max-md:-mt-12 py-10">
    <div
      class="xl:flex gap-[113px] bg-white p-20 max-md:p-4 rounded-3xl md:bg-[url('/images/contact/contact-us-bg.png')] bg-cover bg-no-repeat max-md:bg-[url('/images/contact/contact-us-bg-mobie.png')] max-md:bg-bottom max-md:pb-16 max-md:mt-20"
      data-aos="fade-up"
    >
      <div class="basis-1/2" data-aos="fade-up" data-aos-delay="200">
        <section id="contact-us" class="">
          <div class="max-xl:text-center">
            <h3 class="!text-black text-[50px] font-extrabold uppercase max-md:text-left max-md:text-[32px]">
              let’s connect
            </h3>
            <p class="subtitle-3 text-[16px] !text-black font-medium mt-5 max-md:text-left max-md:text-[14px]">
              Let’s get in touch today! Please fill out the form with your inquiry or you can reach us through the
              contacts below.
            </p>

            <a-form
              ref="formRef"
              class="w-full mt-5 max-xl:text-left"
              layout="vertical"
              :model="formState"
              :rules="rules"
              @finish="onSubmit"
            >
              <a-form-item label="" class="flex-1" name="fullname">
                <a-input v-model:value="formState.fullname" placeholder="Full name *" />
              </a-form-item>

              <a-form-item label="" class="flex-1" name="email">
                <a-input v-model:value="formState.email" placeholder="Email *" />
              </a-form-item>

              <a-form-item label="" class="flex-1" name="phoneNumber">
                <vue-tel-input
                  v-model="formState.phoneNumber"
                  :inputOptions="{ showDialCode: true, tabindex: 0 }"
                  class="h-14 !rounded-[10px] !shadow-none"
                />
              </a-form-item>

              <a-form-item label="" name="message">
                <a-textarea v-model:value="formState.message" placeholder="Message" :rows="5"></a-textarea>
              </a-form-item>

              <a-form-item>
                <a-select
                  v-model:value="formState.howDidYouFindUs"
                  placeholder="How did you find us?"
                  class="black-placeholder"
                >
                  <a-select-option value="Search engine">Search engine (Google, Yahoo, etc)</a-select-option>
                  <a-select-option value="Recommended by friend or colleague">
                    Recommended by friend or colleague
                  </a-select-option>
                  <a-select-option value="Block or publication">Social media</a-select-option>
                  <a-select-option value="Other">Other</a-select-option>
                </a-select>
              </a-form-item>

              <a-form-item class="text-right">
                <button
                  class="primary w-full rounded-[9px] border-none !bg-[#F67D30] text-[16px] font-bold uppercase"
                  type="submit"
                  :disabled="loading"
                >
                  <a-spin :indicator="indicator" size="large" class="spin-white" v-if="loading" />
                  Send
                </button>
              </a-form-item>
            </a-form>

            <a-modal :visible="visibleResultModal" :footer="null" :closable="false">
              <h3 class="text-primary text-center uppercase">Thank You for Reaching Out!</h3>
              <div class="my-10 mx-auto text-center max-w-[500px]">
                We're thrilled to hear from you. We have received your entry and we will get back to you as soon as we
                review it. Have a wonderful day!
              </div>

              <div class="text-center mt-6">
                <button class="primary uppercase" @click="visibleResultModal = false">ok</button>
              </div>
            </a-modal>
          </div>
          <div class="flex flex-wrap gap-10 justify-between max-md:hidden">
            <div class="flex gap-3 items-center" v-for="(contact, index) in contacts">
              <nuxt-picture :src="contact.icon" :alt="contact.title" :width="28" :height="28" />
              <div>
                <h3 class="text-black text-[14px] font-semibold">{{ contact.title }}</h3>
                <p class="text-[14px] font-medium mt-1">{{ contact.content }}</p>
              </div>
            </div>
          </div>
        </section>
      </div>
      <div class="basis-1/2 flex items-center max-xl:mt-6" data-aos="fade-up" data-aos-delay="200">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3725.2290315253917!2d105.77408337494992!3d20.983454389333694!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3135ab71492421e3%3A0x795ad91ea915a4c!2zQ8O0bmcgVHkgQ-G7lSBQaOG6p24gTWlyYXdheSBHaeG6o2kgUGjDoXAgQ8O0bmcgTmdo4buH!5e0!3m2!1sfr!2s!4v1709265345642!5m2!1sfr!2s"
          class="w-full h-[85%] max-md:h-[396px]"
          style="border: 0"
          allowfullscreen=""
          loading="lazy"
          referrerpolicy="no-referrer-when-downgrade"
        ></iframe>
      </div>
    </div>
  </div>
</template>

<style>
.black-placeholder .ant-select-selection-placeholder {
  color: black !important;
}
.spin-white .ant-spin-dot .anticon-spin {
  fill: white !important;
}
.vti__input {
  border-radius: 10px !important;
  outline-color: black !important;
}
.vue-tel-input:focus-within {
  border-color: black !important;
}
.vti__dropdown {
  border-top-left-radius: 10px !important;
  border-bottom-left-radius: 10px !important;
}
::-webkit-scrollbar {
  width: 10px;
}
::-webkit-scrollbar-track {
  background: #f1f1f1;
}
::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
  height: 30px;
}
.vti__dropdown-list.below {
  top: 55px;
}
.ant-modal-content {
  width: max-content !important;
}
</style>
